import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { BackupAttributionCandidate } from '../types/models/BackupVmMapping';
import type { ApiResponseBackupAttributionCandidates } from '../types/api/ApiResponseBackupVmMapping';

/** Server-side scope of the review queue — every field maps to a query parameter. */
export interface BackupAttributionQueueFilters {
  /** Scope to one directory mapping — normally the shared house tree. `null` = every directory. */
  backupMappingId: number | null;
  /** `all=1`: every subject in scope, including settled ones NetBox agrees with. */
  all: boolean;
  /** `include_missing=1`: also subjects whose directory has vanished. Excluded by default. */
  includeMissing: boolean;
}

interface UseBackupAttributionQueueReturn {
  candidates: BackupAttributionCandidate[];
  /**
   * `false` = NetBox could not be reached. NOT an error: the queue still renders and staff can
   * still attribute manually — the page shows a warning banner instead of an error state.
   */
  netboxAvailable: boolean;
  /** A fixed safe string from the backend (never upstream error text), or null. */
  netboxReason: string | null;
  /** The 1000-subject cap was hit. Must be surfaced — a clipped queue is not a reviewed queue. */
  truncated: boolean;
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Drop one subject from the queue after it has been attributed, keyed by `server_backup_id`.
   * Attributing settles the row, so leaving it in the queue would invite a second, now-invalid
   * decision — and a full refetch would flash the spinner over the whole table.
   */
  removeCandidate: (serverBackupId: number) => void;
}

const buildQuery = (filters: BackupAttributionQueueFilters): string => {
  const params = new URLSearchParams();
  if (filters.backupMappingId !== null) {
    params.set('backup_mapping_id', String(filters.backupMappingId));
  }
  if (filters.all) {
    params.set('all', '1');
  }
  if (filters.includeMissing) {
    params.set('include_missing', '1');
  }
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
};

/**
 * GET /admin/backup-vm-mappings/candidates — the per-VM backup attribution review queue.
 *
 * Staff-only on the backend; the page mirrors the gate. `enabled` (the caller's own
 * `is_ancalagon_staff` check) defers the fetch so a non-staff visitor — who is shown <NotFound />
 * — never fires a call the backend would reject.
 *
 * The three filters are SERVER-side (they change what the queue means, not just what is visible),
 * so changing one refetches. Free-text search and pagination are applied client-side on top of the
 * returned set by the page.
 */
export const useBackupAttributionQueue = (
  enabled = true,
  filters: BackupAttributionQueueFilters,
): UseBackupAttributionQueueReturn => {
  const [candidates, setCandidates] = useState<BackupAttributionCandidate[]>([]);
  const [netboxAvailable, setNetboxAvailable] = useState(true);
  const [netboxReason, setNetboxReason] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const { backupMappingId, all, includeMissing } = filters;

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `/admin/backup-vm-mappings/candidates${buildQuery({ backupMappingId, all, includeMissing })}`,
      );
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseBackupAttributionCandidates | null;
      if (body?.status === 'success' && body.data?.candidates) {
        setCandidates(body.data.candidates);
        // A NetBox outage is a degraded success, not a failure — default to "available" only when
        // the backend actually says so.
        setNetboxAvailable(body.data.netbox_available !== false);
        setNetboxReason(body.data.netbox_reason ?? null);
        setTruncated(body.data.truncated === true);
      } else {
        setError(body?.message ?? 'Failed to load the backup attribution queue');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the attribution queue');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, backupMappingId, all, includeMissing]);

  const removeCandidate = useCallback((serverBackupId: number) => {
    setCandidates((prev) => prev.filter((c) => c.server_backup_id !== serverBackupId));
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchCandidates();
    }
  }, [enabled, auth.isAuthenticated, fetchCandidates]);

  return {
    candidates,
    netboxAvailable,
    netboxReason,
    truncated,
    loading,
    error,
    refetch: fetchCandidates,
    removeCandidate,
  };
};
